import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { useGuestSiteController } from './GuestSite.controller';
import { AnalyticsContext, useLocation } from '@shared/core';
import { BrannanNavigation, DesignLayoutType } from '@graphql/generated';
import { ScrollToProvider } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/providers/ScrollToProvider/ScrollToProvider';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type { StaticContext } from '@react-router';
import { Box, JoyKitThemeProvider, useDisclosure } from '@withjoy/joykit';
import JustFonts from '@apps/guest/packages/layout-engine/components/JustFonts';
import { GuestSiteDebugger } from '@apps/guest/components/GuestSiteDebugger';
import { ActionBar } from '@apps/guest/packages/layout-engine/components/ActionBar';
import { UnlockDialogProvider } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { LayoutProvider } from '@shared/context/LayoutProvider';
import { BannerPublishReminder } from '@apps/guest/packages/layout-engine/components/BannerPublishReminder';
import { UnpublishedWebsiteBanner } from '@apps/guest/packages/layout-engine/components/UnpublishedWebsiteBanner';
import { AccommodationsVisibilityBanner } from '@apps/guest/packages/layout-engine/components/AccommodationsVisibilityBanner';
import { wrapWithGuestSiteHealthProvider } from './GuestSite.health';
import { GuestSiteFeatureSet } from '@apps/guest/packages/layout-engine/components/GuestSiteFeatureSet/GuestSiteFeatureSet';
import { useNativeState } from '@shared/core/native';
import { useInformParentOnMousedown } from './hooks/useInformParentOnMousedown';
import { FontPackProvider } from '@apps/guest/packages/layout-engine/layouts/FontPackProvider/FontPackProvider';
import { ColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { SmartAppBanner, SmartAppBannerProvider, useSmartAppBannerProvider } from '@shared/components';
import { usePurchaseContextIdFromLocation } from '@apps/registry/common/utils/dropship';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { DiscountsProvider } from '@shared/components/DiscountsProvider/DiscountsProvider';
import { HotelsDiscountsProvider } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { UnpublishedWebsite } from './components/UnpublishedWebsite/UnpublishedWebsite';
import { StickyNavBarOffsetProvider, useBannersOffset } from '@apps/guest/packages/layout-engine/components/StickyNavBarOffset';
import loadable from '@loadable/component';
import { PremiumHeaderLayoutContext, usePremiumHeaderLayout } from './hooks/usePremiumHeaderLayout';
import { HeaderLayoutUpgradeDialog } from '@apps/admin/routes/WebsiteDesignerV2/components/HeaderLayoutUpgradeDialog';
import { HeaderLayoutUpgradeBanner } from '@apps/guest/packages/layout-engine/components/HeaderLayoutUpgradeBanner';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import { createLazyComponentFromNamedExport } from '@shared/utils/createLazyComponent';

// This component was inadvertantly including all of `thankYouForRsvp` app's code into the guest site bundle.
// - Solved by extracting out shared code into it's own file -- a constant key moved to `shared/constants/bannerKeys.ts`
// - creating a bundle b/c this component is conditionally shown
const StatusBanner = createLazyComponentFromNamedExport(
  () =>
    import(
      /* webpackChunkName: "content/guest/site/statusbanner" */
      './components/StatusBanner/StatusBanner'
    ),
  'StatusBanner',
);

const LayoutAloha = loadable(
  () =>
    import(
      /* webpackChunkName: "content/guest/layouts/aloha" */
      '@apps/guest/packages/layout-engine/layouts/LayoutAloha'
    ),
);

const LayoutBrannan = loadable(
  () =>
    import(
      /* webpackChunkName: "content/guest/layouts/brannan" */
      '@apps/guest/packages/layout-engine/layouts/LayoutBrannan'
    ),
);

const LayoutBrannanSinglePage = createLazyComponentFromNamedExport(
  () =>
    import(
      /* webpackChunkName: "content/guest/layouts/brannan-single-page" */
      '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannanSinglePage'
    ),
  'LayoutBrannanSinglePage',
);

const PreviewListener = loadable(
  () =>
    import(
      /* webpackChunkName: "content/guest/site/previewlistener" */
      '@apps/guest/routes/GuestSite/PreviewListener'
    ),
);

const Announcement = loadable(
  () =>
    import(
      /* webpackChunkName: "content/guest/announcement" */
      '@apps/guest/packages/layout-engine/components/Announcement'
    ),
);

const PurchaseConfirmationDialog = loadable(
  () =>
    import(
      /* webpackChunkName: "content/guest/registry/purchaseconfirmationdialog" */
      '@apps/guest/packages/layout-engine/widgets/Custom/components/Accommodations/components/PurchaseConfirmationDialog/PurchaseConfirmationDialog'
    ),
);

export interface GuestSiteLayoutProps
  extends Readonly<{
    eventHandle: string;
    event: GuestSiteEvent;
    layout: DesignLayoutType;
    setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
    setIsLastPageVisible?: (value: boolean) => void;
    isNativeAccommodationPage: boolean;
  }> {}

const GuestSiteLayout: React.FC<GuestSiteLayoutProps> = ({ eventHandle, event, layout, setLoadingTheme, setIsLastPageVisible, isNativeAccommodationPage }) => {
  const brannanNavigation = event.eventDesign?.brannanNavigation;
  const [isSimpleScrollLayoutEnabled] = useIsFeatureEnabled('websiteSimpleScrollLayoutEnabled');

  const layoutSwitch = () => {
    const layoutTypes = {
      [DesignLayoutType.aloha]: LayoutAloha,
      [DesignLayoutType.brannan]:
        !isNativeAccommodationPage && brannanNavigation === BrannanNavigation.singlePage && isSimpleScrollLayoutEnabled ? LayoutBrannanSinglePage : LayoutBrannan,
    };
    const Layout = layoutTypes[layout];

    return (
      (event && (
        <Layout
          setLoadingTheme={setLoadingTheme}
          setIsLastPageVisible={setIsLastPageVisible}
          eventHandle={eventHandle}
          event={event}
        />
      )) ||
      null
    );
  };

  return layoutSwitch();
};

export interface GuestSiteBodyProps
  extends Readonly<{
    eventHandle: string;
    staticContext: StaticContext | undefined;
  }> {}

const GuestSiteBodyBase: React.FC<GuestSiteBodyProps> = wrapWithGuestSiteHealthProvider(({ eventHandle, staticContext }) => {
  const {
    event,
    layout,
    eventPageRoutes,
    isPreviewing,
    handleOnPreviewUpdate,
    handleOnRefetchData,
    theme,
    loading,
    justFonts,
    graphicAccent,
    shouldRenderActionBar,
    setLoadingTheme,
    setIsLastPageVisible,
    showBannerReminderChanges,
    onDismissReminder,
    onClickReminder,
    showAnnouncement,
    onAnnouncementDismiss,
    isNativeAccommodationPage,
    showUnpublishedPage,
    isLoadingWebsitePublishCapability,
    showUnpublishedBanner,
    handlePublishWebsite,
    isPublishingWebsite,
    headerPresentationLayout,
    showAccommodationsVisibilityBanner,
    handleCloseAccommodationsVisibilityBanner,
  } = useGuestSiteController({
    eventHandle,
    staticContext,
  });
  const { isNative } = useNativeState();

  const { smartAppBannerHeight } = useSmartAppBannerProvider();

  const { pathname } = useLocation();
  const purchaseId = usePurchaseContextIdFromLocation();
  const updateSearchParam = useUpdateSearchParam();
  const analytics = useContext(AnalyticsContext);
  const [isPurchaseConfirmationOpen, setIsPurchaseConfirmationOpen] = useState(false);
  const { isOpen: isPurchaseConfirmationDialogOpen, onClose: onPurchaseConfirmationClose } = useDisclosure({
    isOpen: isPurchaseConfirmationOpen,
    onClose: () => {
      updateSearchParam('pcId', null);
      setIsPurchaseConfirmationOpen(false);
    },
  });
  const guestSiteTelemetry = useGuestSiteTelemetry();

  const isTravelOrAccomPage = useMemo(() => pathname.includes('/travel') || pathname.includes('/accommodation'), [pathname]);
  const shouldRenderPurchaseConfirmationDialog = !!purchaseId && isTravelOrAccomPage;

  useEffect(() => {
    if (shouldRenderPurchaseConfirmationDialog) {
      setIsPurchaseConfirmationOpen(true);
      analytics.track({
        category: 'guest.purchase',
        action: 'PageViewed',
        extraInfo: { name: 'registryPurchaseConfirmation', pageType: pathname.includes('/travel') ? 'travel' : 'accommodation' },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRenderPurchaseConfirmationDialog]);

  // hook to inform irame parent on mousedown
  useInformParentOnMousedown(isPreviewing);

  const premiumHeaderLayout = usePremiumHeaderLayout({ headerPresentationLayout });

  const bannersOffset = useBannersOffset({
    changesReminderBanner: showBannerReminderChanges,
    unpublishedWebsiteBanner: showUnpublishedBanner,
    headerLayoutUpgradeBanner: premiumHeaderLayout.banner.isVisible,
    accommodationsVisibilityBanner: showAccommodationsVisibilityBanner,
  });

  return (
    <>
      <Box paddingTop={Math.max(smartAppBannerHeight, bannersOffset)}>
        {!loading && <StatusBanner />}
        <GuestSiteFeatureSet loadingGuestSite={loading || isLoadingWebsitePublishCapability}>
          {event && showUnpublishedPage ? (
            <UnpublishedWebsite />
          ) : event && layout ? (
            <LayoutProvider
              layout={layout}
              brannanNavigation={event.eventDesign?.brannanNavigation}
            >
              <FontPackProvider eventDesign={event.eventDesign}>
                <UnlockDialogProvider>
                  <ColorPaletteProvider eventDesign={event.eventDesign}>
                    <JoyKitThemeProvider theme={theme}>
                      {showAnnouncement && !isNative && (
                        <Announcement
                          onAnnouncementClosed={() => onAnnouncementDismiss(event.info.announcementBanner.title)}
                          content={<>{event.info.announcementBanner.message}</>}
                          title={event.info.announcementBanner.title}
                          graphicAccent={graphicAccent}
                        />
                      )}
                      {justFonts ? (
                        <JustFonts />
                      ) : (
                        <ScrollToProvider>
                          {isPreviewing && (
                            <PreviewListener
                              eventPageRoutes={eventPageRoutes}
                              eventDesign={event?.eventDesign}
                              onRefetchData={handleOnRefetchData}
                              onUpdate={handleOnPreviewUpdate}
                            />
                          )}
                          <PremiumHeaderLayoutContext.Provider value={premiumHeaderLayout}>
                            <StickyNavBarOffsetProvider offset={bannersOffset}>
                              <GuestSiteLayout
                                setLoadingTheme={setLoadingTheme}
                                eventHandle={eventHandle}
                                event={event}
                                layout={layout}
                                setIsLastPageVisible={setIsLastPageVisible}
                                isNativeAccommodationPage={isNativeAccommodationPage}
                              />
                            </StickyNavBarOffsetProvider>
                          </PremiumHeaderLayoutContext.Provider>
                        </ScrollToProvider>
                      )}
                      {showBannerReminderChanges && (
                        <BannerPublishReminder
                          handleDismiss={onDismissReminder}
                          handleClickLink={onClickReminder}
                          eventHandle={eventHandle}
                        />
                      )}
                      {showUnpublishedBanner && (
                        <UnpublishedWebsiteBanner
                          onPublish={handlePublishWebsite}
                          isPublishing={isPublishingWebsite}
                        />
                      )}
                      {showAccommodationsVisibilityBanner && (
                        <AccommodationsVisibilityBanner
                          onClose={handleCloseAccommodationsVisibilityBanner}
                          eventHandle={eventHandle}
                        />
                      )}
                      {premiumHeaderLayout.banner.isVisible && <HeaderLayoutUpgradeBanner onUpgrade={premiumHeaderLayout.banner.onClick} />}
                      <HeaderLayoutUpgradeDialog
                        isOpen={premiumHeaderLayout.dialog.isOpen}
                        onClose={premiumHeaderLayout.dialog.onClose}
                        secondaryAction={premiumHeaderLayout.dialog.secondaryAction}
                        onDialogOpen={() => guestSiteTelemetry.headerLayoutUpgradeDialog.enter()}
                        onUpgradeClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogUpgradeClicked()}
                        onNoThanksClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogNoThanksClicked()}
                        onAllDoneClick={() => guestSiteTelemetry.headerLayoutUpgradeDialogAllDoneClicked()}
                      />
                    </JoyKitThemeProvider>
                    <GuestSiteDebugger event={event} />
                  </ColorPaletteProvider>
                  {shouldRenderActionBar && (
                    <ActionBar
                      eventId={event.id}
                      showAlways={event.eventDesign?.brannanNavigation === BrannanNavigation.singlePage}
                    />
                  )}
                </UnlockDialogProvider>
              </FontPackProvider>
            </LayoutProvider>
          ) : null}
        </GuestSiteFeatureSet>
      </Box>
      <SmartAppBanner telemetryCategory="wedding" />
      {/* Defer rendering the purchase confirmation dialog because it imports from registry app */}
      {shouldRenderPurchaseConfirmationDialog && (
        <PurchaseConfirmationDialog
          isOpen={isPurchaseConfirmationDialogOpen}
          onClose={onPurchaseConfirmationClose}
        />
      )}
    </>
  );
});

const GuestSiteBody: React.FC<GuestSiteBodyProps> = props => {
  return (
    <DiscountsProvider>
      <HotelsDiscountsProvider>
        <SmartAppBannerProvider>
          <GuestSiteBodyBase {...props} />
        </SmartAppBannerProvider>
      </HotelsDiscountsProvider>
    </DiscountsProvider>
  );
};

GuestSiteBody.displayName = 'GuestSiteBody';

export default GuestSiteBody;
